/**
 * dialogue-enhancer.js — Web Audio graph owner for the /dialogue-enhancer
 * player. Wraps the dialogue-processor AudioWorklet (dialogue-worklet.js,
 * proven by tests/audio/dialogue-enhancer-groundtruth.mjs) with the native
 * post-chain and a stable engine API the blade UI talks to.
 *
 * Graph (built once per attachSource; ctx reused across files):
 *
 *   src(MediaElementSource) ─► worklet ─ out0 dialogue ─► HP100 ─► EQ1.8k ─► EQ3k ─┬► comp ─► compOnG ─┐
 *                              │                                                    └► compOffG ────────┴► dlgGain ─┐
 *                              └ out1 music ─► musGain ──────────────────────────────────────────────────────────────┤
 *                                                                                              wetGain ◄─────────────┘
 *   src ─► dryDelay(=worklet latency) ─► dryGain ──────────────────────────────────────────────┐
 *                                                wet+dry ─► masterGain(boost ≤3×) ─► limiter ─► safety(0.95) ─► dest
 *
 * Presets (Amazon-style Off/Low/Medium/High):
 *   voice gain    +3 / +6 / +9 dB        mask hardness p   1.5 / 2.0 / 3.5
 *   music offset   0 / −3 / −6 dB        duck depth        0.3 / 0.55 / 0.75
 *   EQ 1.8k/3k    +3+2 / +4+3 / +5+4 dB  night comp        off / off / on
 *   'off' = equal-power crossfade to the latency-matched dry path (hold-to-
 *   compare uses the same crossfade, so A/B is sample-aligned and instant).
 *
 * Degraded mode: no AudioWorklet support (legacy WebKit) → 'eq-only' native
 * chain (HP + presence EQ + voice gain, no separation); the UI shows a chip
 * and disables the background slider.
 *
 * All native AudioParam changes go through setTargetAtTime — never `.value=`
 * mid-playback (zipper noise; the one video-volume pattern we do NOT copy).
 */
(function () {
    'use strict';

    var TC = 0.05;          // s, default smoothing time-constant
    var TC_FADE = 0.015;    // s, bypass crossfade (≈50 ms settle)

    var PRESETS = {
        low: { voiceDb: 3, musicDb: 0, eq1: 3, eq2: 2, maskPower: 1.5, duckDepth: 0.30, night: false },
        medium: { voiceDb: 6, musicDb: -3, eq1: 4, eq2: 3, maskPower: 2.0, duckDepth: 0.55, night: false },
        // maskPower 2.5 → 3.5 (measured 2026-08-27, tests/scratch/de-node-bus*):
        // on a decorrelated background the sharper mask rejects 1.75 dB more
        // music for 0.5 dB more speech attenuation, and on a centre-panned
        // background it changes nothing (the mask is saturated there either
        // way). High is the preset people reach for when they still cannot
        // hear the words, so it should trade that way.
        high: { voiceDb: 9, musicDb: -6, eq1: 5, eq2: 4, maskPower: 3.5, duckDepth: 0.75, night: true }
    };

    function dbToLin(db) { return Math.pow(10, db / 20); }

    function DialogueEnhancer(opts) {
        opts = opts || {};
        this.slug = opts.slug || 'dialogue-enhancer';
        this.workletUrls = opts.workletUrls || ['/js/dialogue-worklet.js'];
        // FFT 1024 on low-end devices: half the latency, coarser bins
        this.fftSize = opts.fftSize ||
            (((navigator.deviceMemory || 8) <= 4 || (navigator.hardwareConcurrency || 8) <= 4) ? 1024 : 2048);
        this.ctx = null;
        this._injectedCtx = opts.ctx || null;
        this.workletReady = false;
        this.degraded = false;          // no-AudioWorklet fallback
        this.nodes = null;              // current graph nodes
        this.sourceEl = null;
        this._srcCache = (typeof WeakMap !== 'undefined') ? new WeakMap() : null;
        this.state = {
            mode: 'idle', channels: 0, latencyFrames: 0,
            preset: 'medium', bypass: false, night: false, nightUser: null,
            voiceDb: PRESETS.medium.voiceDb, musicDb: PRESETS.medium.musicDb,
            boost: 1, meters: { dlg: -100, mus: -100 }
        };
        this.metersLog = [];            // [{t, dlg, mus}] ring, E2E + UI meters
        this._modeCbs = [];
        this._meterCbs = [];
    }

    DialogueEnhancer.prototype._ensureCtx = function () {
        if (this.ctx) return this.ctx;
        // Injected context = offline render (dialogue-export.js). Everything
        // downstream is context-agnostic: _buildFull() takes a source node and
        // ends at ctx.destination, so the exported file goes through the exact
        // same chain the user auditioned. Two engines would drift.
        if (this._injectedCtx) { this.ctx = this._injectedCtx; return this.ctx; }
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) throw new Error('WebAudio unavailable');
        try {
            // 48 kHz keeps the worklet's bin math and a future RNNoise insert
            // at their native rate; very old WebKit throws on the options bag.
            this.ctx = new AC({ sampleRate: 48000 });
        } catch (e) {
            this.ctx = new AC();
        }
        // Задержка компрессора — для выравнивания речи и музыки в _buildFull (замер
        // приходит за миллисекунды; граф прослушки строится позже, по нажатию).
        var self = this;
        if (window.NodeLatency) window.NodeLatency.compressor(this.ctx.sampleRate).then(function (l) { self._compLat = l; });
        return this.ctx;
    };

    DialogueEnhancer.prototype._loadWorklet = function () {
        var self = this;
        if (self.workletReady) return Promise.resolve(true);
        if (!self.ctx.audioWorklet || typeof AudioWorkletNode === 'undefined') {
            self.degraded = true;
            return Promise.resolve(false);
        }
        var load = (window.AudioWorkletLoader && window.AudioWorkletLoader.load)
            ? window.AudioWorkletLoader.load(self.ctx, self.workletUrls, { slug: self.slug })
            : self.ctx.audioWorklet.addModule(self.workletUrls[self.workletUrls.length - 1]);
        return load.then(function () {
            self.workletReady = true;
            return true;
        }, function (err) {
            self.degraded = true;
            try { console.warn('[dialogue-enhancer] worklet load failed, degrading to EQ-only:', err && err.message); } catch (_) {}
            return false;
        });
    };

    /** Attach (or re-attach) the engine to a playing media element. */
    DialogueEnhancer.prototype.attachSource = function (mediaEl) {
        var self = this;
        var ctx = self._ensureCtx();
        return self._loadWorklet().then(function () {
            self._teardownGraph();
            // createMediaElementSource is once-per-element and irreversible —
            // cache so a re-attach to the same element reuses the node.
            var src = self._srcCache && self._srcCache.get(mediaEl);
            if (!src) {
                src = ctx.createMediaElementSource(mediaEl);
                if (self._srcCache) self._srcCache.set(mediaEl, src);
            }
            self.sourceEl = mediaEl;
            if (self.degraded) self._buildDegraded(src);
            else self._buildFull(src);
            self._applyAll();
            return self.getState();
        });
    };

    DialogueEnhancer.prototype._buildFull = function (src) {
        var ctx = this.ctx, self = this;
        var n = {};
        n.src = src;
        n.worklet = new AudioWorkletNode(ctx, 'dialogue-processor', {
            numberOfInputs: 1,
            numberOfOutputs: 2,
            outputChannelCount: [2, 2],
            channelCount: 8,
            channelCountMode: 'max',
            channelInterpretation: 'discrete',
            processorOptions: { fftSize: this.fftSize }
        });
        n.worklet.port.onmessage = function (e) { self._onWorkletMsg(e.data); };
        n.worklet.onprocessorerror = function () {
            try { if (window.__aeToolError) window.__aeToolError({ error_type: 'dlg_worklet_crashed', slug: self.slug }); } catch (_) {}
        };

        n.hp = ctx.createBiquadFilter(); n.hp.type = 'highpass'; n.hp.frequency.value = 100; n.hp.Q.value = 0.7;
        n.eq1 = ctx.createBiquadFilter(); n.eq1.type = 'peaking'; n.eq1.frequency.value = 1800; n.eq1.Q.value = 1.1;
        n.eq2 = ctx.createBiquadFilter(); n.eq2.type = 'peaking'; n.eq2.frequency.value = 3000; n.eq2.Q.value = 2.0;
        n.comp = ctx.createDynamicsCompressor();
        n.comp.threshold.value = -35; n.comp.ratio.value = 4; n.comp.attack.value = 0.005;
        n.comp.release.value = 0.25; n.comp.knee.value = 12;
        n.compOnG = ctx.createGain(); n.compOnG.gain.value = 0;
        n.compOffG = ctx.createGain(); n.compOffG.gain.value = 1;
        /* Ночной компрессор речи держит упреждение 6 мс, а музыка и ветка «без
           компрессора» шли мимо — в ночном режиме речь отставала от музыки на 288
           отсчётов (48 кГц). Обе обходные ветки задержаны на то же (замер
           NodeLatency, self._compLat): речь и музыка сходятся в любом режиме. */
        var al = (this._compLat || 0) / ctx.sampleRate;
        n.musAlign = ctx.createDelay(1); n.musAlign.delayTime.value = al;
        n.offAlign = ctx.createDelay(1); n.offAlign.delayTime.value = al;
        n.dlgGain = ctx.createGain();
        n.musGain = ctx.createGain();
        n.wetGain = ctx.createGain(); n.wetGain.gain.value = 1;
        n.dryDelay = ctx.createDelay(0.2);
        n.dryGain = ctx.createGain(); n.dryGain.gain.value = 0;
        n.master = ctx.createGain();
        n.limiter = ctx.createDynamicsCompressor();
        n.limiter.threshold.value = -3; n.limiter.ratio.value = 20; n.limiter.attack.value = 0.001;
        n.limiter.release.value = 0.05; n.limiter.knee.value = 0;
        n.safety = ctx.createGain(); n.safety.gain.value = 0.95;

        src.connect(n.worklet);
        n.worklet.connect(n.hp, 0);
        n.worklet.connect(n.musGain, 1);
        n.hp.connect(n.eq1); n.eq1.connect(n.eq2);
        n.eq2.connect(n.comp); n.comp.connect(n.compOnG); n.compOnG.connect(n.dlgGain);
        n.eq2.connect(n.compOffG); n.compOffG.connect(n.offAlign); n.offAlign.connect(n.dlgGain);
        n.dlgGain.connect(n.wetGain);
        n.musGain.connect(n.musAlign); n.musAlign.connect(n.wetGain);
        src.connect(n.dryDelay); n.dryDelay.connect(n.dryGain);
        n.wetGain.connect(n.master); n.dryGain.connect(n.master);
        n.master.connect(n.limiter); n.limiter.connect(n.safety); n.safety.connect(this.ctx.destination);
        this.nodes = n;
        this.state.mode = 'detecting';
    };

    DialogueEnhancer.prototype._buildDegraded = function (src) {
        // No worklet → no separation. Voice EQ + gain on the full mix so the
        // tool still helps; the blade shows the 'eq-only' chip.
        var ctx = this.ctx;
        var n = {};
        n.src = src;
        n.hp = ctx.createBiquadFilter(); n.hp.type = 'highpass'; n.hp.frequency.value = 100; n.hp.Q.value = 0.7;
        n.eq1 = ctx.createBiquadFilter(); n.eq1.type = 'peaking'; n.eq1.frequency.value = 1800; n.eq1.Q.value = 1.1;
        n.eq2 = ctx.createBiquadFilter(); n.eq2.type = 'peaking'; n.eq2.frequency.value = 3000; n.eq2.Q.value = 2.0;
        n.dlgGain = ctx.createGain();
        n.wetGain = ctx.createGain(); n.wetGain.gain.value = 1;
        n.dryGain = ctx.createGain(); n.dryGain.gain.value = 0;
        n.master = ctx.createGain();
        n.limiter = ctx.createDynamicsCompressor();
        n.limiter.threshold.value = -3; n.limiter.ratio.value = 20; n.limiter.attack.value = 0.001;
        n.limiter.release.value = 0.05; n.limiter.knee.value = 0;
        n.safety = ctx.createGain(); n.safety.gain.value = 0.95;
        src.connect(n.hp); n.hp.connect(n.eq1); n.eq1.connect(n.eq2); n.eq2.connect(n.dlgGain);
        n.dlgGain.connect(n.wetGain);
        src.connect(n.dryGain);
        n.wetGain.connect(n.master); n.dryGain.connect(n.master);
        n.master.connect(n.limiter); n.limiter.connect(n.safety); n.safety.connect(ctx.destination);
        this.nodes = n;
        this.state.mode = 'eq-only';
        this.state.latencyFrames = 0;
        this._emitMode();
    };

    DialogueEnhancer.prototype._onWorkletMsg = function (d) {
        if (!d) return;
        if (d.type === 'info') {
            this.state.mode = d.mode;
            this.state.channels = d.channels;
            this.state.latencyFrames = d.latencyFrames;
            if (this.nodes && this.nodes.dryDelay) {
                // Сухой обход — на задержку ворклета И выравнивания речи/музыки.
                this.nodes.dryDelay.delayTime.value = (d.latencyFrames + (this._compLat || 0)) / this.ctx.sampleRate;
            }
            this._emitMode();
        } else if (d.type === 'meters') {
            this.state.meters = { dlg: d.dlg, mus: d.mus };
            this.metersLog.push({ t: this.sourceEl ? this.sourceEl.currentTime : 0, dlg: d.dlg, mus: d.mus });
            if (this.metersLog.length > 900) this.metersLog.splice(0, 300);
            for (var i = 0; i < this._meterCbs.length; i++) {
                try { this._meterCbs[i](this.state.meters); } catch (_) {}
            }
        }
    };

    DialogueEnhancer.prototype._emitMode = function () {
        for (var i = 0; i < this._modeCbs.length; i++) {
            try { this._modeCbs[i](this.getState()); } catch (_) {}
        }
    };

    /** Push every current setting into the live graph (idempotent). */
    DialogueEnhancer.prototype._applyAll = function () {
        var st = this.state, n = this.nodes;
        if (!n) return;
        var now = this.ctx.currentTime;
        var p = PRESETS[st.preset] || PRESETS.medium;
        var voiceDb = st.preset === 'custom' ? st.voiceDb : p.voiceDb;
        var musicDb = st.preset === 'custom' ? st.musicDb : p.musicDb;
        var night = st.nightUser !== null ? st.nightUser : p.night;
        st.voiceDb = voiceDb; st.musicDb = musicDb; st.night = night;

        n.dlgGain.gain.setTargetAtTime(dbToLin(voiceDb), now, TC);
        if (n.musGain) n.musGain.gain.setTargetAtTime(dbToLin(musicDb), now, TC);
        if (n.eq1) n.eq1.gain.setTargetAtTime(st.preset === 'custom' ? PRESETS.medium.eq1 : p.eq1, now, TC);
        if (n.eq2) n.eq2.gain.setTargetAtTime(st.preset === 'custom' ? PRESETS.medium.eq2 : p.eq2, now, TC);
        if (n.compOnG) {
            n.compOnG.gain.setTargetAtTime(night ? 1 : 0, now, TC);
            n.compOffG.gain.setTargetAtTime(night ? 0 : 1, now, TC);
        }
        n.master.gain.setTargetAtTime(st.boost, now, TC);
        var off = st.bypass || st.preset === 'off';
        if (n.worklet) {
            n.worklet.port.postMessage({
                type: 'params',
                maskPower: st.preset === 'custom' ? PRESETS.medium.maskPower : p.maskPower,
                // Duck OFF while the dry path is what reaches the speakers.
                // The worklet keeps running (its output feeds the meters), and a
                // ducked music meter under an un-ducked mix made the UI read-out
                // claim a balance nobody was hearing — the meter has to describe
                // the audible signal or it is decoration.
                duckDepth: off ? 0 : (st.preset === 'custom' ? PRESETS.medium.duckDepth : p.duckDepth)
            });
        }
        n.wetGain.gain.setTargetAtTime(off ? 0 : 1, now, TC_FADE);
        n.dryGain.gain.setTargetAtTime(off ? 1 : 0, now, TC_FADE);
    };

    DialogueEnhancer.prototype.setPreset = function (name) {
        if (name !== 'off' && name !== 'custom' && !PRESETS[name]) name = 'medium';
        this.state.preset = name;
        this._applyAll();
    };
    DialogueEnhancer.prototype.setVoiceGainDb = function (db) {
        this.state.voiceDb = Math.min(12, Math.max(0, +db || 0));
        this.state.preset = 'custom';
        this._applyAll();
    };
    DialogueEnhancer.prototype.setMusicLevelDb = function (db) {
        this.state.musicDb = Math.min(0, Math.max(-24, +db || 0));
        this.state.preset = 'custom';
        this._applyAll();
    };
    DialogueEnhancer.prototype.setNightMode = function (on) {
        this.state.nightUser = !!on;
        this._applyAll();
    };
    DialogueEnhancer.prototype.setVolumeBoost = function (pct) {
        this.state.boost = Math.min(3, Math.max(1, (+pct || 100) / 100));
        this._applyAll();
    };
    /**
     * Silence this chain without tearing it down — used while the neural lane
     * (dialogue-ai.js) is the one being heard for the analysed fragment. The
     * graph keeps running so the meters still describe the material, and one
     * gain change swaps back the moment the playhead leaves that fragment.
     *
     * NB: attachSource() rebuilds the graph and resets `safety`, so a caller
     * that re-attaches has to re-apply this.
     */
    DialogueEnhancer.prototype.setMute = function (on) {
        this.state.muted = !!on;
        if (!this.nodes || !this.nodes.safety) return;
        this.nodes.safety.gain.setTargetAtTime(on ? 0 : 0.95, this.ctx.currentTime, 0.02);
    };

    DialogueEnhancer.prototype.setBypass = function (on) {
        this.state.bypass = !!on;
        if (this.nodes) {
            var now = this.ctx.currentTime;
            var off = this.state.bypass || this.state.preset === 'off';
            this.nodes.wetGain.gain.setTargetAtTime(off ? 0 : 1, now, TC_FADE);
            this.nodes.dryGain.gain.setTargetAtTime(off ? 1 : 0, now, TC_FADE);
            if (this.nodes.worklet) {
                var pb = PRESETS[this.state.preset] || PRESETS.medium;
                this.nodes.worklet.port.postMessage({ type: 'params', duckDepth: off ? 0 : pb.duckDepth });
            }
        }
    };

    /**
     * Offline render entry: feed an AudioBuffer through the same graph.
     * Only meaningful on an injected OfflineAudioContext. The caller owns
     * startRendering() and the leading-latency trim (see dialogue-export.js) —
     * this method just wires the source and starts it.
     */
    DialogueEnhancer.prototype.attachBuffer = function (audioBuffer) {
        var self = this;
        var ctx = self._ensureCtx();
        return self._loadWorklet().then(function () {
            // Офлайн число задержки нужно ДО сборки графа (выравнивание речи и музыки).
            return window.NodeLatency ? window.NodeLatency.compressor(ctx.sampleRate).then(function (l) { self._compLat = l; }) : null;
        }).then(function () {
            self._teardownGraph();
            var src = ctx.createBufferSource();
            src.buffer = audioBuffer;
            self.sourceEl = null;
            if (self.degraded) self._buildDegraded(src);
            else self._buildFull(src);
            // The worklet's `info` message (channel count → dry-delay) only
            // arrives after rendering finishes, so the dry path can never be
            // latency-matched offline. Export therefore renders WET ONLY and
            // the caller refuses to export with the enhancer switched off.
            if (self.nodes.dryGain) self.nodes.dryGain.gain.value = 0;
            if (self.nodes.wetGain) self.nodes.wetGain.gain.value = 1;
            self._applyAll();
            if (self.nodes.dryGain) self.nodes.dryGain.gain.value = 0;
            if (self.nodes.wetGain) self.nodes.wetGain.gain.value = 1;
            src.start(0);
            return self.getState();
        });
    };

    DialogueEnhancer.prototype.resume = function () {
        if (this.ctx && this.ctx.state === 'suspended') {
            return this.ctx.resume().catch(function () {});
        }
        return Promise.resolve();
    };

    DialogueEnhancer.prototype.getState = function () {
        var st = this.state;
        var lat = st.latencyFrames / (this.ctx ? this.ctx.sampleRate : 48000);
        try {
            lat += (this.ctx && this.ctx.baseLatency) || 0;
            lat += (this.ctx && this.ctx.outputLatency) || 0;
        } catch (_) {}
        return {
            mode: st.mode, channels: st.channels, preset: st.preset,
            bypass: st.bypass, night: st.night, muted: !!st.muted, voiceDb: st.voiceDb,
            musicDb: st.musicDb, boost: st.boost,
            latencyFrames: st.latencyFrames, latencySec: lat,
            degraded: this.degraded, meters: st.meters,
            ctxState: this.ctx ? this.ctx.state : 'none'
        };
    };

    DialogueEnhancer.prototype.onModeChange = function (cb) { this._modeCbs.push(cb); };
    DialogueEnhancer.prototype.onMeters = function (cb) { this._meterCbs.push(cb); };

    DialogueEnhancer.prototype._teardownGraph = function () {
        var n = this.nodes;
        if (!n) return;
        try { if (n.worklet) { n.worklet.port.onmessage = null; } } catch (_) {}
        for (var k in n) {
            if (n[k] && typeof n[k].disconnect === 'function') {
                try { n[k].disconnect(); } catch (_) {}
            }
        }
        this.nodes = null;
        this.state.mode = 'idle';
        this.state.channels = 0;
        this.state.latencyFrames = 0;
    };

    DialogueEnhancer.prototype.destroy = function () {
        this._teardownGraph();
        this._modeCbs = []; this._meterCbs = [];
        // ctx is kept open intentionally: MediaElementSource nodes are
        // per-element-forever and a reused ctx lets "open another file"
        // rebuild instantly without an audio-device round-trip.
    };

    window.DialogueEnhancer = {
        create: function (opts) { return new DialogueEnhancer(opts); },
        PRESETS: PRESETS
    };
})();
